/**
 * CLIENTELE — Phase 2 Revenue Infrastructure Scripts
 * Covers: Auth Badge, Condition Grade Tooltip, Wishlist,
 *         Sell Form, Loyalty Capture, FAQ Accordion, Alerts Form
 *
 * Rules: No jQuery. No frameworks. Pure ES6+.
 * Zero bounce/elastic animations. 200–300ms ease only.
 */

'use strict';

/* ═══════════════════════════════════════════════════════
   MODULE 01 — AUTHENTICATION BADGE TOGGLE
═══════════════════════════════════════════════════════ */
function initAuthBadge() {
  document.querySelectorAll('.c-auth-badge__toggle').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      var target = document.getElementById(btn.getAttribute('aria-controls'));
      if (!target) return;

      btn.setAttribute('aria-expanded', String(!expanded));
      target.setAttribute('aria-hidden', String(expanded));
    });
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 02 — CONDITION GRADE TOOLTIP
═══════════════════════════════════════════════════════ */
function initConditionGrade() {
  document.querySelectorAll('.c-condition__pill').forEach(function(pill) {
    var container = pill.closest('.c-condition');
    var tooltip = container ? container.querySelector('.c-condition__def') : null;
    var hideTimer = null;

    if (!tooltip) return;

    function show() {
      clearTimeout(hideTimer);
      pill.setAttribute('aria-expanded', 'true');
      tooltip.setAttribute('aria-hidden', 'false');
      // Auto-dismiss after 3 seconds
      hideTimer = setTimeout(hide, 3000);
    }

    function hide() {
      clearTimeout(hideTimer);
      pill.setAttribute('aria-expanded', 'false');
      tooltip.setAttribute('aria-hidden', 'true');
    }

    pill.addEventListener('click', function(e) {
      e.stopPropagation();
      var isVisible = pill.getAttribute('aria-expanded') === 'true';
      isVisible ? hide() : show();
    });

    document.addEventListener('click', function() {
      hide();
    });
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 06 — WISHLIST SYSTEM
   localStorage key: 'cl_wishlist'
   Structure: [{id, handle, title, price, image}]
═══════════════════════════════════════════════════════ */
var WISHLIST_KEY = 'cl_wishlist';

function getWishlist() {
  try {
    return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
  } catch(e) {
    return [];
  }
}

function saveWishlist(list) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
  } catch(e) {}
  // Dispatch event so other components can update
  document.dispatchEvent(new CustomEvent('cl:wishlist:updated', { detail: { list: list } }));
}

function isWishlisted(productId) {
  return getWishlist().some(function(p) { return String(p.id) === String(productId); });
}

function addToWishlist(item) {
  var list = getWishlist();
  if (!list.some(function(p) { return String(p.id) === String(item.id); })) {
    list.push(item);
    saveWishlist(list);
  }
}

function removeFromWishlist(productId) {
  var list = getWishlist().filter(function(p) {
    return String(p.id) !== String(productId);
  });
  saveWishlist(list);
}

function toggleWishlist(item) {
  if (isWishlisted(item.id)) {
    removeFromWishlist(item.id);
    return false;
  } else {
    addToWishlist(item);
    return true;
  }
}

function updateWishlistBtn(btn, saved) {
  btn.setAttribute('aria-pressed', String(saved));
  var icon = btn.querySelector('.c-wishlist-save__icon');
  var text = btn.querySelector('.c-wishlist-save__text');
  if (icon) icon.textContent = saved ? '◆' : '◇';
  if (text) text.textContent = saved ? 'SAVED' : 'SAVE';
}

function initWishlistButtons() {
  document.querySelectorAll('[data-wishlist-save]').forEach(function(btn) {
    var productId = btn.getAttribute('data-product-id');
    if (!productId) return;

    // Set initial state
    updateWishlistBtn(btn, isWishlisted(productId));

    btn.addEventListener('click', function() {
      var item = {
        id: productId,
        handle: btn.getAttribute('data-product-handle'),
        title: btn.getAttribute('data-product-title'),
        price: btn.getAttribute('data-product-price'),
        image: btn.getAttribute('data-product-image')
      };
      var saved = toggleWishlist(item);
      updateWishlistBtn(btn, saved);
    });
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 06 — WISHLIST PAGE RENDERER
═══════════════════════════════════════════════════════ */
function initWishlistPage() {
  var container = document.getElementById('c-wishlist-grid');
  var emptyState = document.getElementById('c-wishlist-empty');
  if (!container) return;

  function renderWishlist() {
    var list = getWishlist();
    container.innerHTML = '';

    if (list.length === 0) {
      container.style.display = 'none';
      if (emptyState) emptyState.style.display = 'block';
      return;
    }

    container.style.display = 'grid';
    if (emptyState) emptyState.style.display = 'none';

    list.forEach(function(item) {
      var card = document.createElement('article');
      card.className = 'c-wishlist__card';
      card.innerHTML =
        '<button class="c-wishlist__card-remove" data-remove-id="' + item.id + '" aria-label="Remove from saved">✕ REMOVE</button>' +
        '<a href="/products/' + (item.handle || '') + '">' +
          '<div class="c-wishlist__card-img-wrap">' +
            (item.image
              ? '<img class="c-wishlist__card-img" src="' + item.image + '" alt="' + (item.title || '') + '" loading="lazy">'
              : '<div class="c-wishlist__card-img-wrap" style="position:relative;padding-bottom:100%;background:#111;"></div>'
            ) +
          '</div>' +
        '</a>' +
        '<div class="c-wishlist__card-info">' +
          '<p class="c-wishlist__card-name">' + (item.title || 'UNKNOWN ITEM') + '</p>' +
          (item.price
            ? '<span class="c-wishlist__card-price">' + formatMoney(item.price) + '</span>'
            : ''
          ) +
          '<button class="c-wishlist__card-notify" data-notify-id="' + item.id + '">NOTIFY ME</button>' +
        '</div>';

      card.querySelector('.c-wishlist__card-remove').addEventListener('click', function() {
        removeFromWishlist(item.id);
        renderWishlist();
      });

      container.appendChild(card);
    });
  }

  renderWishlist();

  document.addEventListener('cl:wishlist:updated', renderWishlist);
}

function formatMoney(price) {
  // Liquid raw prices ({{ variant.price }}) and cart AJAX are INTEGER CENTS
  // (9000 = $90.00). Dollar values always carry a decimal point ("90.00").
  // Legacy wishlist localStorage entries stored raw cents — handle both.
  var n = parseFloat(price);
  if (isNaN(n)) return '';
  if (String(price).indexOf('.') === -1) n = n / 100;
  return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/* ═══════════════════════════════════════════════════════
   MODULE 03 — SELL FORM SUBMIT
═══════════════════════════════════════════════════════ */
function initSellForm() {
  var form = document.getElementById('c-sell-form');
  var confirmation = document.getElementById('c-sell-confirmation');
  if (!form) return;

  // Toggle buttons for box question
  form.querySelectorAll('.c-sell__toggle-option').forEach(function(opt) {
    opt.addEventListener('click', function() {
      form.querySelectorAll('.c-sell__toggle-option').forEach(function(o) {
        o.classList.remove('is-active');
      });
      opt.classList.add('is-active');
      var input = form.querySelector('input[name="has_box"]');
      if (input) input.value = opt.getAttribute('data-value');
    });
  });

  form.addEventListener('submit', function(e) {
    e.preventDefault();

    var data = new FormData(form);
    var subject = '[CONSIGNMENT INQUIRY] ' + (data.get('item_name') || 'Unlabeled Item');

    // Build body string for Shopify contact form
    var body = 'CONSIGNMENT SUBMISSION\n\n';
    body += 'Item: ' + (data.get('item_name') || '') + '\n';
    body += 'Brand: ' + (data.get('brand') || '') + '\n';
    body += 'Size: ' + (data.get('size') || '') + '\n';
    body += 'Condition: ' + (data.get('condition') || '') + '\n';
    body += 'Asking Price: $' + (data.get('asking_price') || 'TBD') + '\n';
    body += 'Has Box: ' + (data.get('has_box') || 'Unknown') + '\n';
    body += 'Description: ' + (data.get('description') || '') + '\n';
    body += 'Name: ' + (data.get('name') || '') + '\n';
    body += 'Email: ' + (data.get('email') || '') + '\n';
    body += 'Phone: ' + (data.get('phone') || '') + '\n';
    body += 'Location: ' + (data.get('location') || '') + '\n';

    var shopifyPayload = new URLSearchParams();
    shopifyPayload.append('form_type', 'contact');
    shopifyPayload.append('utf8', '✓');
    shopifyPayload.append('contact[email]', data.get('email') || '');
    shopifyPayload.append('contact[name]', data.get('name') || '');
    shopifyPayload.append('contact[phone]', data.get('phone') || '');
    shopifyPayload.append('contact[subject]', subject);
    shopifyPayload.append('contact[body]', body);

    var submitBtn = form.querySelector('.c-sell__submit');
    if (submitBtn) {
      submitBtn.textContent = 'SENDING...';
      submitBtn.disabled = true;
    }

    fetch('/contact', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: shopifyPayload.toString()
    })
    .then(function(res) {
      form.style.display = 'none';
      if (confirmation) {
        confirmation.classList.add('is-visible');
        confirmation.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    })
    .catch(function(err) {
      if (submitBtn) {
        submitBtn.textContent = 'SUBMIT FOR REVIEW →';
        submitBtn.disabled = false;
      }
      console.error('Sell form error:', err);
    });
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 04 — LOYALTY EMAIL CAPTURE
═══════════════════════════════════════════════════════ */
function initLoyaltyCapture() {
  var form = document.getElementById('c-loyalty-capture-form');
  if (!form) return;

  form.addEventListener('submit', function(e) {
    e.preventDefault();
    var email = form.querySelector('.c-loyalty__capture-input');
    if (!email || !email.value) return;

    var btn = form.querySelector('.c-loyalty__capture-btn');
    if (btn) {
      btn.textContent = 'JOINED';
      btn.disabled = true;
    }
    email.disabled = true;

    // Submit to Klaviyo or newsletter - Shopify newsletter contact form
    var payload = new URLSearchParams();
    payload.append('form_type', 'customer');
    payload.append('utf8', '✓');
    payload.append('customer[email]', email.value);
    payload.append('contact[tags]', 'newsletter,client-tier');

    fetch('/contact#newsletter-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload.toString()
    }).catch(function() {});
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 09 — FAQ ACCORDION
═══════════════════════════════════════════════════════ */
function initFaqAccordion() {
  document.querySelectorAll('.c-faq__question').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var item = btn.closest('.c-faq__item');
      var isActive = item.classList.contains('c-faq__item--active');

      // Close all
      document.querySelectorAll('.c-faq__item--active').forEach(function(el) {
        el.classList.remove('c-faq__item--active');
      });

      // Open clicked if was closed
      if (!isActive) {
        item.classList.add('c-faq__item--active');
        item.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 07 — NOTIFY CAPTURE (sold-out)
═══════════════════════════════════════════════════════ */
function initNotifyCapture() {
  document.querySelectorAll('[data-notify-form]').forEach(function(form) {
    form.addEventListener('submit', function(e) {
      e.preventDefault();
      var input = form.querySelector('.c-notify-capture__input');
      var btn = form.querySelector('.c-notify-capture__btn');
      if (!input || !input.value) return;

      // Submit to Shopify newsletter
      var payload = new URLSearchParams();
      payload.append('form_type', 'customer');
      payload.append('utf8', '✓');
      payload.append('customer[email]', input.value);

      fetch('/contact#newsletter-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString()
      }).finally(function() {
        if (btn) btn.textContent = 'NOTED';
        if (input) input.disabled = true;
        if (btn) btn.disabled = true;
      });
    });
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 07 — ALERTS PAGE EMAIL CAPTURE
═══════════════════════════════════════════════════════ */
function initAlertsCapture() {
  var form = document.getElementById('c-alerts-email-form');
  if (!form) return;

  form.addEventListener('submit', function(e) {
    e.preventDefault();
    var input = form.querySelector('.c-alerts__email-input');
    var btn = form.querySelector('.c-alerts__email-btn');
    if (!input || !input.value) return;

    var payload = new URLSearchParams();
    payload.append('form_type', 'customer');
    payload.append('utf8', '✓');
    payload.append('customer[email]', input.value);

    fetch('/contact#newsletter-form', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: payload.toString()
    }).finally(function() {
      if (btn) btn.textContent = 'JOINED';
      if (btn) btn.disabled = true;
      if (input) input.disabled = true;
    });
  });
}

/* ═══════════════════════════════════════════════════════
   INIT — DOMContentLoaded
═══════════════════════════════════════════════════════ */
document.addEventListener('DOMContentLoaded', function() {
  initAuthBadge();
  initConditionGrade();
  initWishlistButtons();
  initWishlistPage();
  initSellForm();
  initLoyaltyCapture();
  initFaqAccordion();
  initNotifyCapture();
  initAlertsCapture();
});

// Expose wishlist utilities globally for Shopify sections
window.ClienteleWishlist = {
  get: getWishlist,
  add: addToWishlist,
  remove: removeFromWishlist,
  toggle: toggleWishlist,
  has: isWishlisted
};

/* ═══════════════════════════════════════════════════════
   MODULE 10 — DROP TABLE DATE SORT
   Sorts c-drop-row elements by data-date attribute ascending
═══════════════════════════════════════════════════════ */
function initDropTableSort() {
  var tbody = document.getElementById('c-drop-feed-body');
  if (!tbody) return;

  var rows = Array.from(tbody.querySelectorAll('.c-drop-row'));
  if (rows.length < 2) return;

  rows.sort(function(a, b) {
    var dateA = a.getAttribute('data-date') || '';
    var dateB = b.getAttribute('data-date') || '';
    return dateA.localeCompare(dateB);
  });

  rows.forEach(function(row) {
    tbody.appendChild(row);
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 06 — WISHLIST COUNT BADGE
   Updates nav badge and wishlist page count
═══════════════════════════════════════════════════════ */
function updateWishlistCount() {
  var list = getWishlist();
  var count = list.length;

  // Update wishlist page count
  var countEl = document.getElementById('c-wishlist-count');
  if (countEl) {
    countEl.textContent = count === 0 ? '' : count + (count === 1 ? ' ITEM' : ' ITEMS');
  }

  // Update nav badge(s) with data-wishlist-count attr
  document.querySelectorAll('[data-wishlist-count]').forEach(function(el) {
    el.textContent = count > 0 ? count : '';
    el.style.display = count > 0 ? 'inline-block' : 'none';
  });
}

/* ═══════════════════════════════════════════════════════
   MODULE 07 — ANNOUNCEMENT BAR SMS ROTATION
   Adds SMS drop notice to announcement rotation
═══════════════════════════════════════════════════════ */
function initAnnouncementSmsInjection() {
  var bar = document.getElementById('announcementMessages');
  if (!bar) return;

  // Check if SMS notice is already in the bar
  var items = bar.querySelectorAll('.c-announcement-bar__item');
  var hasSms = false;
  items.forEach(function(item) {
    if (item.textContent.indexOf('DROP') !== -1 || item.textContent.indexOf('SMS') !== -1) {
      hasSms = true;
    }
  });

  if (!hasSms && items.length < 5) {
    var smsSpan = document.createElement('span');
    smsSpan.className = 'c-announcement-bar__item';
    smsSpan.setAttribute('role', 'status');
    smsSpan.innerHTML = 'Text <strong>DROP</strong> for instant restock alerts';
    bar.appendChild(smsSpan);
  }
}

// Add to DOMContentLoaded init
document.addEventListener('DOMContentLoaded', function() {
  initDropTableSort();
  updateWishlistCount();
  initAnnouncementSmsInjection();
});

// Re-run count update when wishlist changes
document.addEventListener('cl:wishlist:updated', updateWishlistCount);

/* ═══════════════════════════════════════════════════════
   MODULE 11 — ANALYTICS INFRASTRUCTURE
   Fires GA4 + Meta Pixel events on Shopify actions
   ⚠ MANUAL ACTION REQUIRED: Ensure GA4 Measurement ID
   and Meta Pixel ID are set in layout/theme.liquid
═══════════════════════════════════════════════════════ */
function clTrack(eventName, params) {
  params = params || {};

  // GA4
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }

  // Meta Pixel
  if (typeof window.fbq === 'function') {
    var pixelEventMap = {
      'view_item':        'ViewContent',
      'add_to_cart':      'AddToCart',
      'begin_checkout':   'InitiateCheckout',
      'purchase':         'Purchase',
      'search':           'Search',
      'wishlist_add':     'AddToWishlist',
      'contact_submit':   'Lead',
    };
    var pixelEvent = pixelEventMap[eventName];
    if (pixelEvent) {
      window.fbq('track', pixelEvent, params);
    }
  }
}

function initAnalyticsEvents() {
  // ── Product page: view_item ───────────────────────────────────
  if (document.querySelector('.c-product') && window.__clientele_variants) {
    var productId = document.querySelector('[data-product-id]');
    if (productId) {
      var pid = productId.getAttribute('data-product-id');
      var variants = window.__clientele_variants[pid];
      var firstVariant = variants && variants[0];
      clTrack('view_item', {
        currency:   'USD',
        value:      firstVariant ? parseFloat(firstVariant.price) : 0,
        items: [{
          item_id:    pid,
          item_name:  document.title,
          price:      firstVariant ? parseFloat(firstVariant.price) : 0,
        }]
      });
    }
  }

  // ── Add to cart button click ─────────────────────────────────
  document.querySelectorAll('.c-product__atc-btn').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var section  = btn.closest('[data-product-id]') || btn.closest('.c-product');
      var pid      = section ? (section.getAttribute('data-product-id') || '') : '';
      var priceEl  = section ? section.querySelector('.c-product__price') : null;
      var titleEl  = section ? section.querySelector('.c-product__title') : null;
      clTrack('add_to_cart', {
        currency:  'USD',
        items: [{
          item_id:   pid,
          item_name: titleEl ? titleEl.textContent.trim() : document.title,
        }]
      });
    });
  });

  // ── Wishlist add ─────────────────────────────────────────────
  document.querySelectorAll('[data-wishlist-save]').forEach(function(btn) {
    btn.addEventListener('click', function() {
      var saved = btn.getAttribute('aria-pressed') === 'false'; // will become true
      if (saved) {
        clTrack('wishlist_add', {
          item_id:   btn.getAttribute('data-product-id'),
          item_name: btn.getAttribute('data-product-title'),
        });
      }
    });
  });

  // ── Sell/consignment form lead ────────────────────────────────
  var sellForm = document.getElementById('c-sell-form');
  if (sellForm) {
    sellForm.addEventListener('submit', function() {
      clTrack('contact_submit', { form: 'consignment' });
    });
  }

  // ── FAQ accordion engagement ─────────────────────────────────
  document.querySelectorAll('.c-faq__question').forEach(function(btn) {
    btn.addEventListener('click', function() {
      clTrack('faq_expand', {
        question: btn.querySelector('.c-faq__question-text')
                    ? btn.querySelector('.c-faq__question-text').textContent.trim().slice(0, 60)
                    : ''
      });
    }, { once: true });
  });

  // ── Authentication badge expand ──────────────────────────────
  document.querySelectorAll('.c-auth-badge__toggle').forEach(function(btn) {
    btn.addEventListener('click', function() {
      if (btn.getAttribute('aria-expanded') === 'false') {
        clTrack('auth_badge_expand', {});
      }
    }, { once: true });
  });
}

// ── Shopify cart event listener ───────────────────────────────
document.addEventListener('cart:item-added', function(e) {
  if (e.detail && e.detail.item) {
    var item = e.detail.item;
    clTrack('add_to_cart', {
      currency:  'USD',
      value:     parseFloat(item.price) / 100, // cart AJAX returns integer cents
      items: [{
        item_id:   String(item.product_id),
        item_name: item.product_title,
        price:     parseFloat(item.price) / 100,
        quantity:  item.quantity,
        variant_id: String(item.variant_id),
      }]
    });
  }
});

// ── Search event ──────────────────────────────────────────────
var searchInput = document.getElementById('cl-search-input');
if (searchInput) {
  var searchTimer;
  searchInput.addEventListener('input', function() {
    clearTimeout(searchTimer);
    var q = searchInput.value.trim();
    if (q.length > 2) {
      searchTimer = setTimeout(function() {
        clTrack('search', { search_term: q });
      }, 800);
    }
  });
}

document.addEventListener('DOMContentLoaded', initAnalyticsEvents);
